import jsonpatch from 'fast-json-patch';
import { getByCampaign } from './api';
import { COLLECTION_TYPES } from './constants';
import Asset from '@/core/entity/Asset';

// TODO: type this — Vuex module context and dynamic collection state
/* eslint-disable @typescript-eslint/no-explicit-any */
type Ctx = any;
type CollectionState = Record<string, any>;
/* eslint-enable @typescript-eslint/no-explicit-any */

function getCollection(state: CollectionState, collection: string) {
    return state[collection] ?? {};
}

function getItem(state: CollectionState, collection: string, id: string) {
    const c = getCollection(state, collection);
    return c[id] ?? null;
}

async function loadCollection(commit: Ctx, rootState: Ctx, collection: string) {
    const results = (await getByCampaign(collection, rootState.live.sessionId)) as Record<string, unknown>;
    for (const i in results) {
        commit('add', { collection, item: results[i] });
    }
    return results;
}

const CollectionStore = {
    namespaced: true,
    state: {} as CollectionState,
    getters: {
        getItem: (state: CollectionState) => (collection: string, id: string) => {
            return getItem(state, collection, id);
        },
        getCollection: (state: CollectionState) => (collection: string) => {
            return getCollection(state, collection);
        },
        maps: (state: CollectionState) => () => {
            return getCollection(state, COLLECTION_TYPES.maps);
        },
    },
    mutations: {
        patch(state: CollectionState, { collection, id, patch: patches }: Ctx): void {
            const item = getItem(state, collection, id);

            let resolvedPatches = Array.isArray(patches)
                ? patches
                : Array.isArray(patches?.operations)
                  ? patches.operations
                  : Array.isArray(patches?.Operations)
                    ? patches.Operations
                    : [];

            if (resolvedPatches.length === 0) {
                return;
            }

            // Setting the specific property to track MUST be done before the property exists to add reactivity.
            resolvedPatches = resolvedPatches.reduce((nonAddPatches: Ctx[], patch: Ctx) => {
                if (patch.op === 'add') {
                    const path = patch.path.split('/');
                    path.shift();
                    const addReactivityToThisProperty = path.pop();
                    const existingItemPath = path.reduce((obj: Ctx, pathPoint: string) => {
                        if (obj[pathPoint] == null) {
                            obj[pathPoint] = {};
                        }
                        return obj[pathPoint];
                    }, item);
                    existingItemPath[addReactivityToThisProperty] = patch.value;
                } else {
                    nonAddPatches.push(patch);
                }
                return nonAddPatches;
            }, []);

            jsonpatch.applyPatch(item, resolvedPatches);
        },
        forceReactivityUpdate(state: CollectionState, { collection, item }: Ctx): void {
            if (!Object.prototype.hasOwnProperty.call(state, collection)) {
                state[collection] = {};
            }
            state[collection][item._id] = item;
        },
        add(state: CollectionState, { collection, item }: Ctx): void {
            if (!Object.prototype.hasOwnProperty.call(state, collection)) {
                state[collection] = {};
            }
            state[collection][item._id] = item;
        },
        remove(state: CollectionState, { collection, id }: Ctx): void {
            if (Object.prototype.hasOwnProperty.call(state, collection)) {
                delete state[collection][id];
            }
        },
        purge(state: CollectionState): void {
            for (const collection in state) {
                delete state[collection];
            }
        },
    },
    actions: {
        async update({ dispatch }: Ctx, { collection, id, patch }: Ctx): Promise<unknown> {
            const operations = Array.isArray(patch)
                ? patch
                : Array.isArray(patch?.operations)
                  ? patch.operations
                  : Array.isArray(patch?.Operations)
                    ? patch.Operations
                    : [];

            if (operations.length === 0) {
                return null;
            }
            return await dispatch('live/updateCampaignObject', { collection, id, patch: operations }, { root: true });
        },
        async add({ dispatch }: Ctx, { collection, item }: Ctx): Promise<unknown> {
            return await dispatch('live/addCampaignObject', { collection, item }, { root: true });
        },
        async remove({ dispatch }: Ctx, { collection, id }: Ctx): Promise<unknown> {
            return await dispatch('live/removeCampaignObject', { collection, id }, { root: true });
        },
        async load({ commit, rootState }: Ctx, { collection }: Ctx): Promise<unknown> {
            return await loadCollection(commit, rootState, collection);
        },

        async onAdded({ commit, dispatch }: Ctx, { collection, item }: Ctx): Promise<void> {
            commit('add', { collection, item });
            if (collection == COLLECTION_TYPES.maps) {
                await Asset.updateAll(item.stage.elements.map((e: Ctx) => e.asset));
                const player = await dispatch('players/findPlayerFromProfileId', item._userid, { root: true });
                dispatch('players/changePlayerMap', { mapId: item._id, player }, { root: true });
            }
        },
        async onRemoved({ commit }: Ctx, { collection, id }: Ctx): Promise<void> {
            commit('remove', { collection, id });
        },
        async onUpdated({ commit, state }: Ctx, parameters: Ctx): Promise<void> {
            commit('patch', parameters);
            if (parameters.collection == COLLECTION_TYPES.maps) {
                const item = getItem(state, parameters.collection, parameters.id);
                await Asset.updateAll(item.stage.elements.map((e: Ctx) => e.asset));
            }
        },
        async onLibraryChanged({ commit, dispatch }: Ctx, { operation, objects }: Ctx): Promise<void> {
            const isRemoval = operation === 'rm' || operation === 'rmdir';
            let templatesAddedOrUpdated = false;
            const removedTemplateIds: string[] = [];
            for (const item of objects ?? []) {
                const collection = item._collection;
                if (!collection || collection === 'folder') continue;
                if (isRemoval) {
                    commit('remove', { collection, id: item._id });
                    if (collection === COLLECTION_TYPES.templates) {
                        removedTemplateIds.push(item._id);
                    }
                } else {
                    commit('add', { collection, item });
                    if (collection === COLLECTION_TYPES.maps && item.stage?.elements) {
                        await Asset.updateAll(item.stage.elements.map((e: Ctx) => e.asset));
                    }
                    if (collection === COLLECTION_TYPES.templates) {
                        templatesAddedOrUpdated = true;
                    }
                }
            }
            if (templatesAddedOrUpdated) {
                dispatch('templates/register', null, { root: true });
            }
            if (removedTemplateIds.length) {
                dispatch('templates/unregister', removedTemplateIds, { root: true });
            }
        },
        async onLoad({ commit }: Ctx, { collection, items }: Ctx): Promise<void> {
            for (const i in items) {
                commit('add', { collection, item: items[i] });
            }
        },

        async loadMaps({ commit, rootState }: Ctx): Promise<unknown> {
            return await loadCollection(commit, rootState, COLLECTION_TYPES.maps);
        },
        async reload({ state, commit, rootState }: Ctx): Promise<void> {
            const collections = Object.keys(state);
            commit('purge');
            for (const i in collections) {
                await loadCollection(commit, rootState, collections[i]);
            }
        },
    },
};

export default CollectionStore;
